import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Briefcase, Plus, Trash2, ExternalLink } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { getAccountsTable } from '@/features/accounts/api';
import {
  useContactAccountRoles,
  useSetContactAccountRole,
  useRemoveContactAccountRole,
} from '../hooks/useContactAccountRoles';
import type { ContactRole } from '../api/crmApi';

const ROLE_VALUES: ContactRole[] = [
  'contractual_manager',
  'technical_manager',
  'it_manager',
];

const ROLE_BADGE_CLASSES: Record<ContactRole, string> = {
  contractual_manager: 'bg-blue-100 text-blue-800 border-blue-200',
  technical_manager: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  it_manager: 'bg-violet-100 text-violet-800 border-violet-200',
};

export function ContactAccountRolesCard({
  contactId,
  companyAccountId,
}: {
  contactId: string;
  companyAccountId?: string | null;
}) {
  const { t } = useTranslation('crm');
  const { data: roles = [], isLoading } = useContactAccountRoles(contactId);
  const setRole = useSetContactAccountRole(contactId);
  const removeRole = useRemoveContactAccountRole(contactId);

  const [showAdd, setShowAdd] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedAccountId, setSelectedAccountId] = useState<string>('');
  const [selectedRole, setSelectedRole] = useState<ContactRole>('contractual_manager');

  // Reuse the accounts-table fetch (one-shot pageLength=5000 is the existing pattern).
  const { data: accountsResp } = useQuery({
    queryKey: ['accounts-table', 'role-picker'],
    queryFn: () =>
      getAccountsTable({
        pageNumber: 0,
        pageLength: 5000,
        type: 'customer',
        excludeOrganizations: 'true',
        sortKey: 'name',
        sortDirection: 1,
      }),
    staleTime: 5 * 60 * 1000,
    enabled: showAdd,
  });

  const activeRoles = roles.filter((r) => r.isActive);

  // Scope the picker by the contact's company → linked account (PG-native via
  // companies.account_id). Linked = exact account match; siblings = same NNNN
  // prefix (sub-accounts under the same parent); other = rest. When the
  // contact has no company linkage, scopeKind stays "none" and all accounts
  // appear sorted alphabetically as before.
  const linkedAccountNumber = useMemo(() => {
    if (!companyAccountId || !accountsResp) return null;
    return accountsResp.accounts.find((a) => a.id === companyAccountId)?.account_number ?? null;
  }, [companyAccountId, accountsResp]);

  const parentPrefix = useMemo(() => {
    if (!linkedAccountNumber) return null;
    return linkedAccountNumber.split('-')[0];
  }, [linkedAccountNumber]);

  const scopeKind = (accountNumber: string, accountId: string): 'linked' | 'sibling' | 'other' => {
    if (companyAccountId && accountId === companyAccountId) return 'linked';
    if (parentPrefix && accountNumber.startsWith(parentPrefix)) return 'sibling';
    return 'other';
  };

  const filteredAccounts = useMemo(() => {
    const accounts = accountsResp?.accounts || [];
    const needle = search.trim().toLowerCase();
    const matching = needle
      ? accounts.filter(
          (a) =>
            a.name.toLowerCase().includes(needle) ||
            a.account_number.toLowerCase().includes(needle),
        )
      : accounts;
    // Sort: linked first, then siblings, then alphabetical. Cap at 100 so the
    // dropdown stays usable when no search term narrows it.
    const ranked = matching
      .map((a) => ({ acc: a, kind: scopeKind(a.account_number, a.id) }))
      .sort((a, b) => {
        const score = (k: 'linked' | 'sibling' | 'other') =>
          k === 'linked' ? 0 : k === 'sibling' ? 1 : 2;
        const dk = score(a.kind) - score(b.kind);
        if (dk !== 0) return dk;
        return a.acc.account_number.localeCompare(b.acc.account_number);
      });
    return ranked.slice(0, 100);
  }, [accountsResp, search, companyAccountId, parentPrefix]);

  // Auto-pick the linked account when the picker opens, unless the user
  // already selected something else. Keeps "+ Assign" → click → done as one
  // gesture for the common case (contact at company linked to one account).
  useEffect(() => {
    if (showAdd && companyAccountId && !selectedAccountId) {
      setSelectedAccountId(companyAccountId);
    }
  }, [showAdd, companyAccountId, selectedAccountId]);

  const handleAssign = async () => {
    if (!selectedAccountId) return;
    await setRole.mutateAsync({ accountId: selectedAccountId, role: selectedRole });
    setSelectedAccountId('');
    setSearch('');
    setShowAdd(false);
  };

  return (
    <Card>
      <CardHeader className="pb-3 flex flex-row items-center justify-between">
        <CardTitle className="text-base flex items-center gap-1.5">
          <Briefcase className="h-4 w-4" />
          {t('roles.cardTitle')}
        </CardTitle>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setShowAdd((v) => !v)}
        >
          <Plus className="h-4 w-4 mr-1" />
          {showAdd ? t('roles.cancelAssign') : t('roles.assign')}
        </Button>
      </CardHeader>
      <CardContent className="space-y-3">
        {showAdd && (
          <div className="space-y-2 rounded-md border bg-muted/30 p-3">
            <div>
              <label className="text-xs font-medium text-muted-foreground">{t('roles.accountLabel')}</label>
              <Input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t('roles.accountPlaceholder')}
                className="h-8 text-sm mt-1"
              />
              <Select value={selectedAccountId} onValueChange={setSelectedAccountId}>
                <SelectTrigger className="h-8 text-sm mt-1">
                  <SelectValue placeholder={t('roles.pickAccount')} />
                </SelectTrigger>
                <SelectContent>
                  {filteredAccounts.length === 0 ? (
                    <div className="px-3 py-2 text-xs text-muted-foreground">{t('common.noMatches')}</div>
                  ) : (
                    filteredAccounts.map(({ acc, kind }) => (
                      <SelectItem key={acc.id} value={acc.id}>
                        <span className="font-mono text-xs mr-2">{acc.account_number}</span>
                        {acc.name}
                        {kind === 'linked' && (
                          <span className="ml-2 inline-flex items-center rounded border border-blue-200 bg-blue-50 px-1.5 py-0.5 text-[10px] font-medium text-blue-700">
                            {t('roles.linkedBadge')}
                          </span>
                        )}
                        {kind === 'sibling' && (
                          <span className="ml-2 inline-flex items-center rounded border border-emerald-200 bg-emerald-50 px-1.5 py-0.5 text-[10px] font-medium text-emerald-700">
                            {t('roles.siblingBadge')}
                          </span>
                        )}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">{t('roles.roleLabel')}</label>
              <Select value={selectedRole} onValueChange={(v) => setSelectedRole(v as ContactRole)}>
                <SelectTrigger className="h-8 text-sm mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ROLE_VALUES.map((roleVal) => (
                    <SelectItem key={roleVal} value={roleVal}>
                      {t(`roles.${roleVal}_full`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button
              size="sm"
              className="w-full"
              onClick={handleAssign}
              disabled={!selectedAccountId || setRole.isPending}
            >
              {setRole.isPending ? t('roles.assigning') : t('roles.assignButton')}
            </Button>
          </div>
        )}

        {isLoading && (
          <div className="space-y-2">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        )}

        {!isLoading && activeRoles.length === 0 && !showAdd && (
          <p className="text-sm text-muted-foreground text-center py-2">
            {t('roles.noRoles')}
          </p>
        )}

        {activeRoles.map((r) => (
          <div
            key={r.id}
            className="flex items-center justify-between gap-2 rounded-md border bg-card px-3 py-2"
          >
            <div className="min-w-0 flex-1">
              <Link
                to={`/accounts/${r.accountId}`}
                className="flex items-center gap-1.5 text-sm font-medium hover:underline text-foreground"
              >
                <span className="font-mono text-xs text-muted-foreground">
                  {r.accountNumber}
                </span>
                <span className="truncate">{r.accountName}</span>
                <ExternalLink className="h-3 w-3 text-muted-foreground" />
              </Link>
              <Badge
                variant="outline"
                className={`mt-1 text-[10px] ${ROLE_BADGE_CLASSES[r.role]}`}
              >
                {t(`roles.${r.role}`)}
              </Badge>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() =>
                removeRole.mutate({ accountId: r.accountId, role: r.role })
              }
              disabled={removeRole.isPending}
              title={t('roles.removeTitle')}
            >
              <Trash2 className="h-4 w-4 text-muted-foreground" />
            </Button>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
